import { Stack } from '@mantine/core';
import {
    SettingsInfoRow,
    SettingsInfoRowText,
    SettingsLabel,
    SettingsSection,
    SettingsSectionBodyDivider,
    SettingsSectionItem,
    SettingsSectionItemBody,
    SettingsSectionItemHeader,
    SettingsSectionItemHeaderLabel,
} from '@root/Design/Settings';
import { EventEmitter, useEvent } from '@root/Services/EventEmitter';
import { ComponentType, Fragment, ReactNode, useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, InfoCircle } from 'tabler-icons-react';
import { ICustomInputSpec, InputSpec, IWrappedInputSpec } from './InputSpec';
import { SettingsInput, SettingsLabeled, useRerenderOnChange } from './SettingsInputs';

type FormInput = InputSpec;
interface IFormDivider {
    type: 'divider';
}

interface IFormComponentElement<TProps> {
    type: 'component';
    component: ComponentType<TProps>;
    props: TProps;
}

export type IFormCustomElement<V, P> = ICustomInputSpec<V, P> | IWrappedInputSpec<V, P>;

export interface IConditionalFormElement {
    type: 'conditional';
    /** Condition for displaying the elements. */
    condition: () => boolean;
    /** EventEmitter to signal reevaluation of condition and show/hide elements */
    changeEmitter: EventEmitter<void>;
    /** Elements to show/hide based on result of condition */
    elements: FormElement[];
}

export interface FormInfo {
    type: 'info';
    content: Array<string>;
    status?: 'info' | 'warning' | 'error';
    label?: string;
    icon?: ReactNode | null;
}

export interface IDynamicElement {
    type: 'dynamic';
    element: () => Array<FormSectionItem | FormElement>;
    changeEmitter: EventEmitter<void>;
}

export type FormElement =
    | IFormDivider
    | FormInput
    | IFormComponentElement<any>
    | IFormCustomElement<any, any>
    | IConditionalFormElement
    | FormInfo
    | IDynamicElement;

export interface FormSectionItem {
    header?: { label: string; controls?: ReactNode };
    elements: FormElement[];
}

export interface CustomFormSectionItem {
    component: ComponentType<any>;
}

export interface FormSection {
    title?: string;
    elements: Array<FormElement | FormSectionItem>;
    collapsed?: boolean;
}
export function SettingsForm(props: { sections: FormSection[]; onChanged?: (element: FormElement) => void }) {
    return (
        <Stack spacing="md">
            {props.sections.map((section, i) => (
                <SettingsFormSection key={i} section={section} onChanged={props.onChanged} />
            ))}
        </Stack>
    );
}

function SettingsFormSection(props: { section: FormSection; onChanged?: (element: FormElement) => void }) {
    const { onChanged, section } = props;
    const { title, elements } = section;
    return (
        <SettingsSection title={title} collapsed={section.collapsed}>
            {elements.map((element, i) =>
                'elements' in element ? (
                    <SettingsFormSectionItem key={i} sectionItem={element} onChanged={onChanged} />
                ) : (
                    <SettingsFormElement key={i} element={element} onChanged={onChanged} />
                )
            )}
        </SettingsSection>
    );
}

function SettingsFormSectionItem({ sectionItem, onChanged }: { sectionItem: FormSectionItem; onChanged?: (element: FormElement) => void }) {
    const { header, elements } = sectionItem;
    return (
        <SettingsSectionItem>
            {!header ? null : (
                <SettingsSectionItemHeader>
                    <SettingsSectionItemHeaderLabel>{header.label}</SettingsSectionItemHeaderLabel>
                    {header.controls}
                </SettingsSectionItemHeader>
            )}
            <SettingsSectionItemBody>
                {elements.map((element, i) => (
                    <SettingsFormElement key={i} element={element} onChanged={onChanged} />
                ))}
            </SettingsSectionItemBody>
        </SettingsSectionItem>
    );
}

export function SettingsFormElement(props: { element: FormElement; onChanged?: (element: FormElement) => void }) {
    const { element, onChanged } = props;

    switch (element.type) {
        case 'divider':
            return <SettingsSectionBodyDivider />;
        case 'custom':
        case 'wrapped':
            return <CustomFormElement element={element} onChanged={onChanged} />;
        case 'component':
            return <ComponentFormElement element={element} />;
        case 'conditional':
            return <ConditionalFormElement element={element} onChanged={onChanged} />;
        case 'dynamic':
            return <DynamicElement element={element} onChanged={onChanged} />;
        case 'info':
            return <SettingsInfo element={element} />;
        default:
            return <SettingsInput spec={element} onChanged={onChanged} />;
    }
}

function ConditionalFormElement(props: { element: IConditionalFormElement; onChanged?: (element: FormElement) => void }) {
    const { element, onChanged } = props;
    const { changeEmitter, condition, elements } = element;
    useEvent(changeEmitter);
    return <>{!condition() ? null : elements.map((el, i) => <SettingsFormElement key={i} element={el} onChanged={onChanged} />)}</>;
}

function ComponentFormElement(props: { element: IFormComponentElement<any> }) {
    const { component: CustomComponent, props: elProps } = props.element;
    return <CustomComponent {...elProps} />;
}

function CustomFormElement<V, P>({
    element,
    onChanged,
}: {
    element: IWrappedInputSpec<V, P> | ICustomInputSpec<V, P>;
    onChanged?: (element: FormElement) => void;
}) {
    const { type, component: CustomComponent, props: elProps } = element;
    const passthruProps = typeof elProps === 'function' ? ((elProps as () => P)() as P) : elProps;
    const bindingProps = useRerenderOnChange(element as unknown as InputSpec, onChanged);
    const UntypedComponent = CustomComponent as ComponentType<any>;
    const props = { ...passthruProps, ...bindingProps };
    if (type === 'wrapped') {
        return (
            <SettingsLabeled spec={element}>
                <UntypedComponent {...props} />
            </SettingsLabeled>
        );
    } else {
        return <UntypedComponent {...props} />;
    }
}

function DynamicElement(props: { element: IDynamicElement; onChanged?: (element: FormElement) => void }) {
    const { element, onChanged } = props;
    const { changeEmitter, element: elementFn } = element;
    const [currentElements, setCurrentElements] = useState<ReturnType<typeof elementFn>>([]);
    useEffect(() => {
        setCurrentElements(elementFn());
        const { dispose } = changeEmitter.listen(() => setCurrentElements(elementFn()));
        return dispose;
    }, [changeEmitter, elementFn]);

    return (
        <>
            {currentElements.map((el, i) => (
                <Fragment key={i}>
                    {'type' in el ? (
                        <SettingsFormElement element={el} onChanged={onChanged} />
                    ) : (
                        <SettingsFormSectionItem sectionItem={el} onChanged={onChanged} />
                    )}
                </Fragment>
            ))}
        </>
    );
}

function SettingsInfo(props: { element: FormInfo }) {
    const { content, icon: iconOverride, label, status } = props.element;
    const icon = iconOverride ? (
        iconOverride
    ) : status === 'info' ? (
        <InfoCircle />
    ) : status === 'error' ? (
        <AlertCircle />
    ) : status === 'warning' ? (
        <AlertTriangle />
    ) : null;
    return (
        <SettingsInfoRow status={status}>
            {!label ? null : (
                <SettingsLabel strokeWidth={2} icon={icon}>
                    {label}
                </SettingsLabel>
            )}
            {content?.map((text, i) => (
                <SettingsInfoRowText key={i} indent>
                    {text}
                </SettingsInfoRowText>
            ))}
        </SettingsInfoRow>
    );
}
